import React, { useState, useEffect } from 'react';
import { Calendar, List, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { useMsal } from '@azure/msal-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { getCalendarEvents } from '../lib/msGraph';

interface CalendarEvent {
  id: string;
  subject: string;
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
}

interface AppointmentCalendarProps {
  events?: CalendarEvent[];
}

type ViewMode = 'list' | 'calendar';

const REFRESH_INTERVAL = 60000; // Refresh every minute

// Helper function to convert UTC to local time
const convertToLocalTime = (dateTimeString: string): Date => {
  // Add 2 hours to convert from UTC to local time (UTC+2)
  const date = new Date(dateTimeString);
  date.setHours(date.getHours() + 2);
  return date;
};

export default function AppointmentCalendar({ events: externalEvents }: AppointmentCalendarProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [microsoftEvents, setMicrosoftEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const { instance, accounts } = useMsal();
  const { user } = useAuth();

  const fetchMicrosoftCalendar = async () => {
    if (!accounts[0]) {
      console.debug('[Calendar] No Microsoft account found');
      return;
    }

    setLoading(true);
    try {
      // Debug: MSAL Status
      console.debug('[Calendar] MSAL Status:', {
        accounts,
        activeAccount: instance.getActiveAccount(),
        isAuthenticated: accounts.length > 0,
      });

      // Ensure we have an active account
      const activeAccount = instance.getActiveAccount() || accounts[0];
      if (!activeAccount) {
        console.warn('[Calendar] No active Microsoft account found');
        return;
      }

      // Get first and last day of current month
      const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

      // Debug: Calendar fetch parameters
      console.debug('[Calendar] Fetching Calendar:', {
        firstDay,
        lastDay,
        activeAccount: {
          username: activeAccount.username,
          localAccountId: activeAccount.localAccountId,
        },
      });

      const events = await getCalendarEvents(firstDay, lastDay);

      // Debug: Calendar response
      console.log('[Calendar Debug] Microsoft Calendar Response:', {
        eventsCount: events.length,
        dateRange: {
          firstDay: firstDay.toLocaleDateString('de-DE'),
          lastDay: lastDay.toLocaleDateString('de-DE'),
        },
        events: events.map((event) => ({
          id: event.id,
          subject: event.subject,
          start: event.start.dateTime,
          end: event.end.dateTime,
          startLocal: convertToLocalTime(event.start.dateTime).toLocaleString('de-DE'),
          endLocal: convertToLocalTime(event.end.dateTime).toLocaleString('de-DE'),
        })),
      });

      setMicrosoftEvents(events);
    } catch (error: any) {
      console.error('[Calendar] Error fetching calendar:', error);
      toast.error('Fehler beim Laden des Microsoft Kalenders');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and setup periodic refresh
  useEffect(() => {
    console.debug('[Calendar] Component mounted', {
      hasAccounts: accounts.length > 0,
      currentMonth: currentMonth.toISOString(),
    });

    if (accounts[0]) {
      fetchMicrosoftCalendar();

      // Set up periodic refresh
      const intervalId = setInterval(fetchMicrosoftCalendar, REFRESH_INTERVAL);

      // Debug: Interval setup
      console.debug('[Calendar] Refresh interval set up', {
        intervalId,
        refreshInterval: REFRESH_INTERVAL,
      });

      // Cleanup interval on unmount
      return () => {
        console.debug('[Calendar] Cleaning up interval', { intervalId });
        clearInterval(intervalId);
      };
    }
  }, [currentMonth, accounts, instance]);

  // Debug: State changes
  useEffect(() => {
    console.debug('[Calendar] State updated', {
      microsoftEventsCount: microsoftEvents.length,
      externalEventsCount: externalEvents?.length || 0,
      viewMode,
      currentMonth: currentMonth.toISOString(),
    });
  }, [microsoftEvents, externalEvents, viewMode, currentMonth]);

  // Combine external events with Microsoft events
  const allEvents = [...(externalEvents || []), ...microsoftEvents];

  // Debug: Log all events
  console.log('[Calendar Debug] All events:', {
    externalEvents: externalEvents?.length || 0,
    microsoftEvents: microsoftEvents.length,
    totalEvents: allEvents.length,
    events: allEvents.map((event) => ({
      id: event.id,
      subject: event.subject,
      start: event.start.dateTime,
      end: event.end.dateTime,
      startLocal: convertToLocalTime(event.start.dateTime).toLocaleString('de-DE'),
      endLocal: convertToLocalTime(event.end.dateTime).toLocaleString('de-DE'),
    })),
  });

  // Filter out past events (but keep ongoing events)
  const now = new Date();
  const futureEvents = allEvents.filter((event) => {
    // Use the same time conversion as display (+2 hours)
    const eventEnd = convertToLocalTime(event.end.dateTime);
    const isFuture = eventEnd > now;

    // Debug: Log filtering decision
    console.log(`[Calendar Debug] Event "${event.subject}":`, {
      eventEndOriginal: event.end.dateTime,
      eventEndLocal: eventEnd.toLocaleString('de-DE'),
      now: now.toLocaleString('de-DE'),
      isFuture,
      willShow: isFuture,
    });

    return isFuture; // Show events that haven't ended yet
  });

  // Debug: Log filtered results
  console.log('[Calendar Debug] Filtered events:', {
    originalCount: allEvents.length,
    filteredCount: futureEvents.length,
    filteredEvents: futureEvents.map((event) => ({
      id: event.id,
      subject: event.subject,
      start: convertToLocalTime(event.start.dateTime).toLocaleString('de-DE'),
      end: convertToLocalTime(event.end.dateTime).toLocaleString('de-DE'),
    })),
  });

  const sortedEvents = [...futureEvents].sort((a, b) => new Date(a.start.dateTime).getTime() - new Date(b.start.dateTime).getTime());

  const groupEventsByDate = () => {
    const grouped = new Map<string, CalendarEvent[]>();

    sortedEvents.forEach((event) => {
      const localDate = convertToLocalTime(event.start.dateTime);
      const date = localDate.toLocaleDateString('de-DE');
      if (!grouped.has(date)) {
        grouped.set(date, []);
      }
      grouped.get(date)?.push(event);
    });

    return grouped;
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getMonthData = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const weeks: (number | null)[][] = [];
    let currentWeek: (number | null)[] = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      currentWeek.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      currentWeek.push(day);
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    // Add empty cells for remaining days
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }

    return weeks;
  };

  const getEventsForDay = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return sortedEvents.filter((event) => {
      const eventDate = convertToLocalTime(event.start.dateTime);
      return eventDate.getDate() === day && eventDate.getMonth() === date.getMonth() && eventDate.getFullYear() === date.getFullYear();
    });
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const groupedEvents = groupEventsByDate();
  const weeks = getMonthData();

  return (
    <div className="bg-white shadow-lg rounded-lg border-2 border-gray-200 p-6 sticky top-6" style={{ height: 'calc(100vh - 6rem)' }}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-medium text-gray-900 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-green-500" />
          Terminübersicht
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-green-100 text-green-600' : 'text-gray-600 hover:bg-gray-100'}`}
            title="Listenansicht"
          >
            <List className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={`p-2 rounded-md ${viewMode === 'calendar' ? 'bg-green-100 text-green-600' : 'text-gray-600 hover:bg-gray-100'}`}
            title="Kalenderansicht"
          >
            <Calendar className="w-5 h-5" />
          </button>
        </div>
      </div>

      {!accounts[0] && (
        <div className="mb-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-md">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Microsoft Kalender nicht verbunden. Verbinden Sie Ihr Microsoft-Konto, um Ihre Kalendertermine zu sehen.
              </p>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
        </div>
      )}

      {viewMode === 'list' ? (
        <div className="space-y-6 overflow-y-auto pr-2" style={{ maxHeight: 'calc(100% - 4rem)' }}>
          {Array.from(groupedEvents.entries()).map(([date, dayEvents]) => (
            <div key={date} className="border-b border-gray-200 pb-4 last:border-b-0">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{date}</h3>
              <div className="space-y-3">
                {dayEvents.map((event) => {
                  const startTime = convertToLocalTime(event.start.dateTime);
                  const endTime = convertToLocalTime(event.end.dateTime);
                  return (
                    <div key={event.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <p className="text-sm font-medium text-gray-900">
                        {startTime.toLocaleTimeString('de-DE', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}{' '}
                        -{' '}
                        {endTime.toLocaleTimeString('de-DE', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}{' '}
                        Uhr
                      </p>
                      <p className="text-sm text-gray-600 mt-1">{event.subject}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {groupedEvents.size === 0 && <div className="text-center text-gray-500 py-8">Keine Termine gefunden</div>}
        </div>
      ) : (
        <div className="overflow-y-auto pr-2" style={{ maxHeight: 'calc(100% - 4rem)' }}>
          <div className="flex items-center justify-between mb-4">
            <button onClick={previousMonth} className="p-2 rounded-md hover:bg-gray-100">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-medium">{currentMonth.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</h3>
            <button onClick={nextMonth} className="p-2 rounded-md hover:bg-gray-100">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day) => (
              <div key={day} className="text-center text-sm font-medium text-gray-700 py-2">
                {day}
              </div>
            ))}

            {weeks.map((week, weekIndex) => (
              <React.Fragment key={weekIndex}>
                {week.map((day, dayIndex) => {
                  const events = day ? getEventsForDay(day) : [];
                  const isToday =
                    day &&
                    new Date().getDate() === day &&
                    new Date().getMonth() === currentMonth.getMonth() &&
                    new Date().getFullYear() === currentMonth.getFullYear();

                  return (
                    <div
                      key={`${weekIndex}-${dayIndex}`}
                      className={`min-h-[100px] border rounded-md p-1 ${day ? 'bg-white' : 'bg-gray-50'} ${
                        isToday ? 'border-green-500' : 'border-gray-200'
                      }`}
                    >
                      {day && (
                        <>
                          <div className={`text-right text-sm ${isToday ? 'text-green-600 font-semibold' : 'text-gray-600'}`}>{day}</div>
                          <div className="mt-1 space-y-1">
                            {events.map((event) => {
                              const startTime = convertToLocalTime(event.start.dateTime);
                              return (
                                <div
                                  key={event.id}
                                  className="text-xs bg-green-50 text-green-700 rounded p-1 truncate"
                                  title={`${event.subject} (${startTime.toLocaleTimeString('de-DE', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })} Uhr)`}
                                >
                                  {startTime.toLocaleTimeString('de-DE', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}{' '}
                                  - {event.subject}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
